import type { JSX } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { IntakeAnswer } from "@companion/shared";
import { Screen } from "../components/ui/editorial/Screen.js";
import { Topline } from "../components/ui/editorial/Topline.js";
import { PrimaryButton } from "../components/ui/editorial/PrimaryButton.js";
import { CuratorBubble } from "../components/ui/editorial/CuratorBubble.js";
import { getTelegram } from "../lib/telegram.js";
import { trpc } from "../trpc.js";

interface Question {
  /** Стабильный идентификатор вопроса — уходит в БД, по нему LLM ориентируется. */
  key: string;
  kicker: string;
  intro: string;
  signature: string;
  title: JSX.Element;
  hint: string;
  placeholder: string;
}

const FIRST_QUESTION: Question = {
  key: "evening_preference",
  kicker: "Спутник · вопрос первый",
  intro:
    "«Начну с простого. Расскажите, как проходит ваш хороший вечер — чтобы я представил ритм.»",
  signature: "Спутник",
  title: (
    <>
      Какой вечер
      <br />
      вам <em className="italic text-velvet">ближе</em>?
    </>
  ),
  hint: "пару строк — достаточно",
  placeholder: "тихий ужин, книга до полуночи, долгая прогулка после работы…",
};

// Сократили с 10 до 3 — ровно столько, чтобы куратор мог сразу начать подбор.
// Остальные тонкости добираются в чате с куратором, когда это уместно.
const QUESTIONS: Question[] = [
  FIRST_QUESTION,
  {
    key: "values_in_person",
    kicker: "Спутник · вопрос второй",
    intro: "«Характер тоньше анкеты. Что вам важно в человеке — и что быстро отталкивает?»",
    signature: "Спутник",
    title: (
      <>
        Что цените
        <br />в <em className="italic text-velvet">человеке</em>?
      </>
    ),
    hint: "не список качеств — своими словами",
    placeholder: "внимание к деталям и тихий юмор; сложно с теми, кто спешит…",
  },
  {
    key: "looking_for",
    kicker: "Спутник · вопрос третий",
    intro: "«Последний — и самый важный. Что вы ищете в знакомстве? Не идеал — что-то настоящее.»",
    signature: "Спутник",
    title: (
      <>
        Чего вы
        <br />
        <em className="italic text-velvet">ищете</em>?
      </>
    ),
    hint: "без формулировок из анкет",
    placeholder: "человека, рядом с которым не хочется торопить время…",
  },
];

// Минимум, чтобы LLM могла хоть что-то извлечь. Короче 20 — уже шум.
const MIN_CHARS = 20;
// Точка, после которой прогресс заполнен: текст уже достаточно содержательный.
const IDEAL_CHARS = 180;
const MAX_CHARS = 1200;

function progressHint(len: number): string {
  if (len < MIN_CHARS) return "ещё побольше — нейросеть лучше поймёт по деталям";
  if (len < 90) return "уже видно характер — можно глубже";
  if (len < IDEAL_CHARS) return "отлично — достаточно для подбора";
  return "спасибо, дальше";
}

export function Onboarding(): JSX.Element {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);

  const utils = trpc.useUtils();
  const submit = trpc.profile.submitIntake.useMutation({
    onSuccess: async () => {
      await utils.me.get.invalidate().catch(() => {
        /* noop — invalidation errors are non-critical */
      });
      navigate("/waiting");
    },
    onError: (e) => {
      // MVP-демо: сетка недоступна → не блокируем юзера, всё равно ведём через waiting.
      const msg = e.message;
      if (/Failed to fetch|NetworkError|ECONNREFUSED/i.test(msg)) {
        navigate("/waiting");
        return;
      }
      setError(msg || "не удалось сохранить");
    },
  });

  const total = QUESTIONS.length;
  const q = QUESTIONS[step] ?? FIRST_QUESTION;
  const value = answers[step] ?? "";
  const trimmed = value.trim();

  const canProceed = trimmed.length >= MIN_CHARS && !submit.isPending;

  const onChange = (v: string) => {
    setAnswers((prev) => ({ ...prev, [step]: v.slice(0, MAX_CHARS) }));
  };

  const onNext = () => {
    setError(null);
    if (step + 1 < total) {
      setStep(step + 1);
      return;
    }
    // Финальный шаг — собираем массив ответов и отправляем.
    const payload: IntakeAnswer[] = QUESTIONS.map((question, idx): IntakeAnswer => {
      const text = (answers[idx] ?? "").trim();
      return { questionKey: question.key, picks: [] as string[], own: text };
    }).filter((a) => (a.own?.length ?? 0) >= MIN_CHARS);

    if (payload.length === 0) {
      setError("пустой ответ");
      return;
    }

    if (!getTelegram()) {
      // Dev/preview — без API, просто переход через waiting.
      navigate("/waiting");
      return;
    }
    submit.mutate({ answers: payload });
  };

  const onBack = () => {
    if (step > 0) {
      setError(null);
      setStep(step - 1);
    }
  };

  return (
    <Screen tone="paper">
      <div className="px-[22px] pt-[48px]">
        <Topline
          left="COMPANION"
          right={`${String(step + 1).padStart(2, "0")} / ${String(total).padStart(2, "0")}`}
        />
      </div>

      <div className="flex flex-1 flex-col px-[22px] pt-[30px]">
        <div className="editorial-kicker text-accent">{q.kicker}</div>

        <div className="mt-[18px] pr-[18px]">
          <CuratorBubble signature={q.signature}>{q.intro}</CuratorBubble>
        </div>

        <h2 className="mt-[28px] font-display font-light leading-[1.02] tracking-[-0.022em] text-ink text-[38px]">
          {q.title}
        </h2>

        <div className="mt-[14px] italic font-display font-light text-[13px] text-ink/55">
          {q.hint}
        </div>

        <div className="mt-[18px] border-l-2 border-accent/60 pl-[14px]">
          <textarea
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            placeholder={q.placeholder}
            className="block w-full min-h-[140px] resize-none bg-transparent font-display italic font-light text-[18px] leading-[1.5] text-ink outline-none placeholder:text-ink/35"
          />
          {/* Прогресс-шкала: подсказывает юзеру, что двух слов маловато. */}
          <div className="mt-[10px]">
            <div className="relative h-[2px] w-full bg-ink/10">
              <div
                className="absolute inset-y-0 left-0 bg-accent transition-[width] duration-300"
                style={{
                  width: `${String(Math.min(100, (trimmed.length / IDEAL_CHARS) * 100))}%`,
                }}
              />
            </div>
            <div className="mt-[6px] flex items-center justify-between editorial-meta text-ink/50">
              <span
                className={trimmed.length < MIN_CHARS ? "italic text-accent/80" : "text-ink/55"}
              >
                {progressHint(trimmed.length)}
              </span>
              <span className="text-ink/35">
                {value.length} / {MAX_CHARS}
              </span>
            </div>
          </div>
        </div>

        {error && <div className="mt-[14px] editorial-meta text-velvet">{error}</div>}
      </div>

      <div className="px-[22px] pb-[24px] pt-[16px]">
        <div className="mb-[18px] flex items-center gap-[14px] editorial-meta text-ink/55">
          <button
            type="button"
            onClick={onBack}
            disabled={step === 0}
            aria-label="Назад"
            className="font-display italic text-[14px] text-ink/50 disabled:opacity-30"
          >
            ←
          </button>
          <span>{String(step + 1).padStart(2, "0")}</span>
          <span className="relative flex-1 h-px bg-ink/14">
            <span
              className="absolute inset-y-0 left-0 bg-accent transition-[width] duration-300"
              style={{ width: `${String(((step + 1) / total) * 100)}%` }}
            />
          </span>
          <span>{String(total).padStart(2, "0")}</span>
        </div>
        <PrimaryButton disabled={!canProceed} onClick={onNext}>
          {submit.isPending ? "…" : step + 1 < total ? "Дальше" : "Готово"}
        </PrimaryButton>
      </div>
    </Screen>
  );
}
